import { buildAggregateResult } from "../analytics/engine/aggregates";
import { groupBy } from "../analytics/engine/groupBy";
import { computeTrend } from "../analytics/engine/trend";
import { buildOverview, buildDataQuality } from "./datasetIntelligence";

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function pickCategoricalDimension(schema) {
  const excluded = new Set([...(schema.time || []), ...(schema.ids || [])]);
  return (schema.dimensions || []).find(d => !excluded.has(d)) || null;
}

// Builds the dashboard's persistent, high-level view of the dataset: real
// KPIs, structural overview, and data quality. This is deliberately NOT a
// specific analytical chart — per-question visualization belongs to chat,
// not the dashboard (which answers "what should I know about this
// dataset?", not "what's the answer to X?").
export const buildDashboard = (data, schema) => {

  if (!data?.length) return null;

  const kpis = [
    { title: "Total Records", value: data.length.toLocaleString() },
  ];

  const measure = schema?.measures?.[0];
  const dimension = pickCategoricalDimension(schema || {});

  if (measure) {

    const sum = buildAggregateResult("sum", data, measure);
    const avg = buildAggregateResult("average", data, measure);

    if (sum.success) {
      kpis.push({ title: `Total ${measure}`, value: fmt(sum.value) });
    }

    if (avg.success) {
      kpis.push({ title: `Average ${measure}`, value: fmt(avg.value) });
    }
  }

  if (measure && dimension) {

    const grouped = groupBy(data, dimension, measure, "sum")
      .filter(g => g.value !== null)
      .sort((a, b) => b.value - a.value);

    if (grouped.length) {

      const total = grouped.reduce((a, g) => a + g.value, 0);
      const top = grouped[0];
      const share = total ? (top.value / total) * 100 : null;

      kpis.push({
        title: `Top ${dimension}`,
        value: String(top.group),
        subtext: share !== null ? `${fmt(share)}% of total` : undefined,
      });
    }
  }

  if (measure && schema?.time?.length) {

    const trend = computeTrend(data, {
      dateColumn: schema.time[0],
      measure,
      period: "year",
      aggregation: "sum",
    });

    if (trend.success && trend.pctChange !== null) {

      const arrow = trend.direction === "increasing" ? "+" : trend.direction === "decreasing" ? "-" : "";

      kpis.push({
        title: `${measure} Trend`,
        value: `${arrow}${fmt(Math.abs(trend.pctChange))}%`,
        subtext: `${trend.direction} since ${trend.series[0].period}`,
      });
    }
  }

  // Fallback for datasets with no detected measures/dimensions at all —
  // still give something useful rather than nothing.
  if (kpis.length === 1) {

    const columns = Object.keys(data[0]);

    const numericColumns = columns.filter(col =>
      data.some(row => !isNaN(parseFloat(row[col])))
    );

    kpis.push({ title: "Columns", value: columns.length });
    kpis.push({ title: "Numeric Fields", value: numericColumns.length });
  }

  return {
    kpis: kpis.slice(0, 5),
    overview: buildOverview(schema || {}, data),
    dataQuality: buildDataQuality(data, schema || {}),
  };
};
